import React, { useEffect, useState } from 'react';
import { ScrollView } from '@progress/kendo-react-scrollview';
import { FileApi } from '../../../../redux/api/FileApi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PartialSpinny, Spinny } from '../../../Nav/Spinny';
import { SvgIcon } from '@progress/kendo-react-common';
import * as svg from '@progress/kendo-svg-icons';
import { IFile } from '../../../../types/File/IFile';
import {
    InputSuffix,
    TextBox,
} from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { FileType } from '../../../../types/File/FileType';
import { UserApi } from '../../../../redux/api/UserApi';

export type AttachmentProps = {
    gotAttachments?: (files: IFile[]) => void;
    showSearch?: boolean;
    showInfo?: boolean;
    showPaging?: boolean;
}

type AttachmentSearch = {
    type: string;
    search: string;
}

const AttachmentsLightBox: React.FC<AttachmentProps> = (props) => {
    const navigate = useNavigate();
    const { actionId, ticketId, id } = useParams();
    const { data: actionfiles, isFetching } = FileApi.useGetFilesQuery({ typeId: FileType.Call_Action_Internal, primaryId: parseInt(actionId) }, { skip: isNaN(parseInt(actionId)) });
    const { data: ticketFiles, isFetching: fetchingTicektFiles } = FileApi.useGetTicketFilesQuery({ ticketId: parseInt(ticketId) }, { skip: !isNaN(parseInt(actionId)) });
    const imageType = 'image/';
    const [downloadUrl, setDownloadUrl] = useState('');
    const [visible, setVisible] = useState(true);
    const showFiles = ticketFiles || actionfiles;
    const [search, setSearch] = useState({} as AttachmentSearch);
    const { data: userProfile } = UserApi.useGetUserProfileQuery();
    const [deleteFile, { isLoading: deletingFile }] = FileApi.useDeleteFileMutation();

    const filteredFiles = showFiles?.filter(f => {
        return (!search.search || f.File_Name.toLowerCase().indexOf(search.search.toLowerCase()) > -1) &&
            (!search.type || f.Type?.indexOf(search.type) > -1)
    });

    //Move to this file slide
    const viewIndex = id && filteredFiles.indexOf(filteredFiles.find(f => f.ID == parseInt(id))) + 1;


    useEffect(() => {
        //Need to re-0render the view when the filter changes otherwise the viewIndex will be wrong
        //This appears to be a buig in the ScrollView component
        // console.warn('This appears to be a buig in the ScrollView component', search);
        setVisible(false);
        setTimeout(() => setVisible(true), 20);
    }, [search]);

    useEffect(() => {
        if (props && props.gotAttachments && filteredFiles) {
            props.gotAttachments(filteredFiles);
        }
    }, [filteredFiles]);

    const getSvgIcon = (type: string) => {
        switch (type) {
            case 'image/png':
            case 'image/jpeg':
            case 'image/jpg':
                return svg.imageIcon;
            case 'application/pdf':
                return svg.filePdfIcon;
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            case 'application/msword':
                return svg.fileWordIcon;
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            case 'application/vnd.ms-excel':
                return svg.fileExcelIcon;
            case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            case 'application/vnd.ms-powerpoint':
                return svg.fileWordIcon;
            case 'video/mp4':
                return svg.fileVideoIcon;
            case 'application/zip':
            case 'application/x-rar-compressed':
            case 'application/x-zip-compressed':
                return svg.fileZipIcon;
            case 'text/html':
            case 'application/json':
            case 'text/xml':
                return svg.fileProgrammingIcon;
            case 'text/csv':
                return svg.fileCsvIcon;
            default:
                if (type.indexOf('text/') > -1) return svg.fileTxtIcon;
                return svg.fileIcon;
        }
    }

    const searchTypes = [
        { name: 'Images', value: 'image/' },
        { name: 'PDF', value: 'application/pdf' },
        { name: 'Documents', value: 'text/' },
        { name: 'Videos', value: 'video/' },
        { name: 'Audio', value: 'audio/' },
        { name: 'Zip', value: 'application/x-zip-compressed' },
        { name: 'Application', value: 'application/' }
    ];

    if (!showFiles || isFetching || deletingFile) return <PartialSpinny />;


    return (
        <div className="attachment-lightbox">
            {props.showSearch && <div className='search-bar d-flex'>
                <div className='col-8'>
                    <TextBox value={search?.search} size='large' placeholder='Search' onChange={(e) => setSearch((s) => {
                        return { ...s, search: e.target.value } as AttachmentSearch
                    })}
                        prefix={() => (
                            <InputSuffix>
                                <Button
                                    themeColor="primary"
                                    fillMode={'flat'}
                                    rounded={null}
                                    svgIcon={svg.searchIcon}
                                />
                            </InputSuffix>
                        )}

                        suffix={() => (
                            <InputSuffix>
                                {search?.search && <Button svgIcon={svg.xIcon} fillMode='flat' onClick={() => {
                                    setSearch((s) => {
                                        return { ...s, search: '' } as AttachmentSearch
                                    })
                                }}></Button>}
                            </InputSuffix>
                        )} />
                </div>
                <div className='col-4' >
                    <ComboBox size='large' allowCustom={false} placeholder='File Type' data={searchTypes} textField='name' key='value'
                        onChange={(e) => setSearch((s) => {
                            return { ...s, type: e.target.value?.value } as AttachmentSearch
                        })}
                    />
                </div></div>}
            {filteredFiles?.length == 0 ?
                <div className='text-center mt-5'><h4>No attachments found</h4></div>
                : visible && <ScrollView activeView={viewIndex} className='scollview' automaticViewChange={false} arrows={true} pageable={props?.showPaging && (filteredFiles?.length > 1)} style={{
                }}>
                    {filteredFiles?.map((item, index) => {
                        const isImage = item.Type?.indexOf(imageType) > -1;
                        return <div className='scroll-item' key={index}>
                            {isImage ?
                                <>

                                    <Link to={props.showSearch ? item.File_Link_Safe : `files/${item.ID}`} title='Attachment preview' className='attachment-preview-image'>
                                        <img className="ms-auto mr-auto" src={item.Image_Url + '?preview=true'} alt={`Attachment ${index + 1}`} style={{
                                            maxHeight: 380
                                        }} draggable={false} loading='lazy' />
                                    </Link>
                                </>
                                : <></>//<h5><SvgIcon icon={getSvgIcon(item.Type)} className='m-auto' size='xxxlarge' /> {item.File_Name}</h5>}
                            }
                            <div className='file-name'>
                                <Link download={item.File_Name} style={{ textDecoration: 'underline' }} className='nav-link' to={item.File_Link_Safe} title='Click to download'>
                                    <h5 className='d-inline'>{!isImage && <div><SvgIcon className='attachment-icon' icon={getSvgIcon(item.Type)} size='xxxlarge' ></SvgIcon></div>} {item.File_Name}</h5>
                                </Link>
                                {userProfile?.Features.Delete_Attachments && <div className='button-wrapper'><Button className='delete-button position-absolute p-1' title='Delete attachment' svgIcon={svg.trashIcon} size='large' fillMode='flat'
                                    onClick={(event) => {
                                        //Delete attachments
                                        if (window.confirm('Are you sure you want to delete this attachment?')) {
                                            deleteFile(item.ID.toString()).unwrap().then(() => {

                                            });
                                        }
                                    }} /></div>}
                            </div>
                            {props && props.showInfo &&
                                <ul className='file-info'>
                                    <li>Type: {item.Type}</li>
                                    <li>Created: {new Date(item.Created + 'Z').toLocaleString()}</li>
                                    <li>Size: {(item.Length / 1024 / 1024).toFixed(2)}Mb</li>
                                </ul>}
                        </div>
                    })}
                </ScrollView>}
            <iframe src={downloadUrl} className='hidden'></iframe>
            {/* <h6>Attachment x of y</h6> */}
        </div>

    );
};

export default AttachmentsLightBox;